import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import OrderApi from "../Api/OrderApi";
import { formatDate } from "../helper/DateHelper";

const ReturnOrderDetailsLayer = () => {
    const { id } = useParams();
    const [orderData, setOrderData] = useState(null);
    const [customerData, setCustomerData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [newStatus, setNewStatus] = useState("");
    const billRef = useRef();

    const fetchDetails = async () => {
        setLoading(true);
        try {
            const res = await OrderApi.getReturnOrderDetails(id);
            if (res.status && res.response) {
                setOrderData(res.response.data.order);
                setCustomerData(res.response.data.customer);
                setNewStatus(res.response.data.order.orderStatus);
            }
        } catch (error) {
            console.error("Error fetching return order details:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDetails();
    }, [id]);

    const handleUpdateStatus = async () => {
        if (!newStatus) return;
        setUpdating(true);
        try {
            const res = await OrderApi.updateReturnOrderStatus(id, newStatus);
            if (res.status) {
                fetchDetails();
            }
        } finally {
            setUpdating(false);
        }
    };

    const handlePrint = () => {
        const printContent = billRef.current.innerHTML;
        const originalContent = document.body.innerHTML;
        document.body.innerHTML = printContent;
        window.print();
        document.body.innerHTML = originalContent;
        window.location.reload();
    };

    if (loading) return (
        <div className="d-flex justify-content-center align-items-center py-100">
            <div className="spinner-border text-primary-600" role="status"></div>
        </div>
    );

    if (!orderData) return <div className="text-center py-100">Return order not found.</div>;

    const statusSteps = ["Return-Initiated", "Approved", "Pickedup", "Received to Warehouse"];
    const currentStatusIndex = statusSteps.indexOf(orderData.orderStatus);
    const progressWidth = (currentStatusIndex / (statusSteps.length - 1)) * 100;

    return (
        <div className="row g-4 pb-40">
            <style>{`
                .order-stepper {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    position: relative;
                    padding: 20px 0 40px;
                    max-width: 900px;
                    margin: 0 auto;
                }
                .stepper-bg {
                    position: absolute;
                    top: 42px;
                    left: 0;
                    right: 0;
                    height: 6px;
                    background: #f1f5f9;
                    z-index: 1;
                    border-radius: 10px;
                }
                .stepper-progress {
                    position: absolute;
                    top: 42px;
                    left: 0;
                    height: 6px;
                    background: linear-gradient(90deg, #6c5ce7, #a29bfe);
                    z-index: 2;
                    transition: width 0.8s;
                    border-radius: 10px;
                }
                .step-item {
                    position: relative;
                    z-index: 3;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 12px;
                    width: 140px;
                }
                .step-circle {
                    width: 48px;
                    height: 48px;
                    border-radius: 50%;
                    background: #fff;
                    border: 4px solid #f1f5f9;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s;
                }
                .step-item.active .step-circle, .step-item.completed .step-circle {
                    border-color: #6c5ce7;
                    background: #6c5ce7;
                    color: #fff;
                }
                .step-label {
                    font-size: 11px;
                    font-weight: 700;
                    color: #64748b;
                    text-align: center;
                }
                .step-item.active .step-label, .step-item.completed .step-label {
                    color: #6c5ce7;
                }
                .pos-bill-container {
                    background: #fff;
                    padding: 30px;
                    border: 1px solid #e2e8f0;
                    border-radius: 0;
                    width: 100%;
                    max-width: 450px;
                    margin: 0 auto;
                    font-family: 'Inter', sans-serif;
                    box-shadow: 0 10px 15px -3px rgba(0,0,0,0.05);
                }
                .bill-header-text {
                    text-align: center;
                    margin-bottom: 24px;
                    border-bottom: 1px dashed #cbd5e1;
                    padding-bottom: 24px;
                }
                .dashed-divider {
                    border-top: 1px dashed #cbd5e1;
                    margin: 16px 0;
                }
                @media print {
                    .no-print { display: none !important; }
                    .pos-bill-container { box-shadow: none; border: none; padding: 0; max-width: 100%; }
                }
                .pos-bill-container table thead,
                .pos-bill-container table thead tr {
                    background: transparent !important;
                    background-color: transparent !important;
                }
                .pos-bill-container table tbody tr {
                    background: transparent !important;
                    background-color: transparent !important;
                }
            `}</style>

            <div className="col-12">
                <div className="card border-0 shadow-sm radius-12 p-32">
                    <div className="d-flex align-items-center justify-content-between mb-40">
                        <div className="d-flex align-items-center gap-3">
                            <Link to="/return-orders" className="btn btn-outline-neutral-600 w-48-px h-48-px d-flex justify-content-center align-items-center rounded-circle text-xl">
                                <Icon icon="lucide:arrow-left" />
                            </Link>
                            <div>
                                <h4 className="mb-1 fw-bold text-primary-600">Return #{(orderData.id || orderData._id)?.toString().slice(-6).toUpperCase()}</h4>
                                <p className="text-sm text-secondary-light mb-0">Initiated on {formatDate(orderData.createdAt)}</p>
                                <p className="text-xs text-secondary-400 mt-1">Orig Order ID: <span className="fw-bold">#{orderData.orderIdString}</span></p>
                            </div>
                        </div>
                        <span className="badge radius-8 px-20 py-10 fw-black text-sm bg-info-focus text-info-main mt-0">
                            {orderData.orderStatus.toUpperCase()}
                        </span>
                    </div>

                    <div className="order-stepper">
                        <div className="stepper-bg"></div>
                        <div className="stepper-progress" style={{ width: `${progressWidth}%` }}></div>
                        {statusSteps.map((step, index) => {
                            const isCompleted = index < currentStatusIndex;
                            const isActive = index === currentStatusIndex;
                            return (
                                <div key={step} className={`step-item ${isActive ? "active" : ""} ${isCompleted ? "completed" : ""}`}>
                                    <div className="step-circle">
                                        <Icon icon={isActive || isCompleted ? "solar:check-read-linear" : "solar:reorder-bold"} className="text-2xl" />
                                    </div>
                                    <span className="step-label">{step}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="col-xl-8">
                <div className="card border-0 shadow-sm radius-12 p-32 h-100">
                    <div className="d-flex align-items-center justify-content-between mb-32">
                        <h6 className="mb-0 fw-bold text-primary-600 d-flex align-items-center gap-2">
                            <Icon icon="solar:bill-list-bold-duotone" className="text-2xl" />
                            Return Order Receipt
                        </h6>
                        <button onClick={handlePrint} className="btn btn-primary-600 radius-8 px-24 py-11 fw-bold d-flex align-items-center gap-2">
                            <Icon icon="solar:printer-bold" className="text-xl" />
                            Print Details
                        </button>
                    </div>

                    <div ref={billRef}>
                        <div className="pos-bill-container">
                            <div className="bill-header-text text-center">
                                <img src="/assets/images/you-leggings-logo.png" alt="Logo" style={{ width: '140px', marginBottom: '8px', objectFit: 'contain' }} />
                                <p className="text-xs text-secondary-light fw-bold mb-1">5/4, Surya Nagar, 2nd Street,</p>
                                <p className="text-xs text-secondary-500 mb-1">Bridgeway Colony Extn, Tirupur - 641607</p>
                                <p className="text-xs text-secondary-500 mb-0">PH: +91 740143 2496 | youleggings@gmail.com</p>
                                <div className="mt-8">
                                    <span style={{ fontSize: '10px' }} className="border border-secondary-100 text-secondary-light px-12 py-4 radius-4 fw-bold uppercase letter-spacing-1">Return Invoice</span>
                                </div>
                                <div className="mt-12 p-8 radius-4 bg-warning-50 border border-warning-100">
                                    <p className="text-warning-600 fw-bold uppercase mb-4" style={{ fontSize: '9px', letterSpacing: '1px' }}>Return Request</p>
                                    <p className="text-secondary-light fw-medium italic mb-0" style={{ fontSize: '11px' }}>Reason: {orderData.returnReason || 'Exchange/Return Requested'}</p>
                                </div>
                            </div>

                            <div className="bill-info-grid">
                                <div className="d-flex justify-content-between mb-4">
                                    <span className="text-xs text-secondary-500 fw-medium">Return ID:</span>
                                    <span className="text-xs text-secondary-light fw-bold">#{(orderData.id || orderData._id)?.toString().slice(-6).toUpperCase()}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-4">
                                    <span className="text-xs text-secondary-500 fw-medium">Original Invoice ID:</span>
                                    <span className="text-xs text-secondary-light fw-bold">#{orderData.invoiceId || orderData.orderIdString}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-0">
                                    <span className="text-xs text-secondary-550 fw-medium">Date:</span>
                                    <span className="text-xs text-secondary-light fw-bold">{formatDate(orderData.createdAt)}</span>
                                </div>
                                <div className="dashed-divider my-12"></div>
                                <div className="mb-12">
                                    <span style={{ fontSize: '10px' }} className="text-secondary-400 fw-bold d-block mb-3 uppercase">Pickup Details:</span>
                                    <h6 className="text-sm fw-bold text-secondary-light mb-1">{orderData.address?.name}</h6>
                                    <p className="text-xs text-secondary-500 mb-0 leading-relaxed">
                                        {orderData.address?.doorNo}, {orderData.address?.street},<br />
                                        {orderData.address?.city} - {orderData.address?.pincode}
                                    </p>
                                    <p className="text-xs text-secondary-550 mt-4 mb-0 fw-bold">Mob: {orderData.address?.phone}</p>
                                </div>
                            </div>

                            <table className="w-100 mb-12" style={{ backgroundColor: 'transparent' }}>
                                <thead className="border-bottom-1 border-secondary-500" style={{ background: 'transparent' }}>
                                    <tr style={{ background: 'transparent' }}>
                                        <th className="text-xs text-secondary-light fw-bold pb-8 text-center uppercase" style={{ width: '30px', minWidth: '30px' }}>S.No</th>
                                        <th className="text-xs text-secondary-light fw-bold pb-8 text-start uppercase" style={{ width: '100%' }}>Items</th>
                                        <th className="text-xs text-secondary-light fw-bold pb-8 text-center uppercase" style={{ width: '60px' }}>Qty</th>
                                        <th className="text-xs text-secondary-light fw-bold pb-8 text-end uppercase" style={{ width: '80px' }}>Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orderData.products.map((item, idx) => (
                                        <tr key={idx} style={{ background: 'transparent' }}>
                                            <td className="py-12 border-bottom-1 border-secondary-50 text-center">
                                                <span className="text-xs text-secondary-light fw-medium">{idx + 1}</span>
                                            </td>
                                            <td className="py-12 border-bottom-1 border-secondary-50">
                                                <span className="text-xs fw-bold text-secondary-light d-block">{item.productName}</span>
                                                {item.combination && item.combination.length > 0 && (
                                                    <div className="d-flex flex-wrap gap-1 mt-4" style={{ fontSize: "7px" }}>
                                                        {item.combination.map((c, i) => (
                                                            <span key={i} className="text-xxxxs border border-secondary-100 px-4 py-1 radius-4 text-secondary-400">
                                                                {c.value}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="py-12 border-bottom-1 border-secondary-50 text-center">
                                                <span className="text-xs text-secondary-light fw-medium">{item.qty}</span>
                                            </td>
                                            <td className="py-12 border-bottom-1 border-secondary-50 text-end">
                                                <span className="text-xs fw-bold text-secondary-light">₹{item.total?.toLocaleString()}</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            <div className="bill-calculation">
                                <div className="d-flex justify-content-between mb-8">
                                    <span className="text-xs text-secondary-550 fw-medium">Subtotal</span>
                                    <span className="text-xs text-secondary-light fw-bold">₹{orderData.totalAmount?.toLocaleString()}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-8">
                                    <span className="text-xs text-secondary-550 fw-medium">Tax</span>
                                    <span className="text-xs text-secondary-light fw-bold">₹{orderData.taxAmount?.toLocaleString()}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-8">
                                    <span className="text-xs text-secondary-550 fw-medium">Shipping (Deducted/Adjusted)</span>
                                    <span className="text-xs text-secondary-light fw-bold">₹{orderData.shippingCharge?.toLocaleString()}</span>
                                </div>
                                <div className="dashed-divider my-12"></div>
                                <div className="d-flex justify-content-between align-items-center">
                                    <span className="text-sm fw-bold text-secondary-light uppercase">Total Refundable</span>
                                    <span className="text-lg fw-bold text-secondary-light">₹{orderData.grandTotal?.toLocaleString()}</span>
                                </div>
                                <div className="mt-20 text-center border-top-1 border-secondary-100 pt-16">
                                    <p className="text-xxxxs text-secondary-400 fst-italic mb-4 uppercase">Status: {orderData.orderStatus}</p>
                                    <p className="text-xxxxs fw-bold text-secondary-light uppercase">*** Thank you! ***</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="col-xl-4 no-print">
                <div className="d-flex flex-column gap-4">
                    <div className="card border-0 shadow-sm radius-12 p-32">
                        <h6 className="mb-16 fw-bold text-primary-600">Update Return Status</h6>
                        <div className="d-flex flex-column gap-3">
                            <select
                                className="form-select radius-8 h-48-px fw-medium text-secondary-light"
                                value={newStatus}
                                onChange={(e) => setNewStatus(e.target.value)}
                            >
                                {statusSteps.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                            <button
                                onClick={handleUpdateStatus}
                                className="btn btn-primary-600 radius-8 h-48-px fw-bold d-flex align-items-center justify-content-center gap-2"
                                disabled={updating}
                            >
                                {updating ? <div className="spinner-border spinner-border-sm"></div> : <Icon icon="solar:refresh-circle-bold" className="text-xl" />}
                                Update Return Status
                            </button>
                        </div>
                    </div>

                    <div className="card border-0 shadow-sm radius-12 p-32">
                        <h6 className="mb-20 fw-bold text-primary-600 d-flex align-items-center gap-2">
                            <Icon icon="solar:user-bold" className="text-xl" />
                            Customer Info
                        </h6>
                        {customerData && (
                            <div className="d-flex flex-column gap-4">
                                <div>
                                    <p className="text-xxxxs text-secondary-400 mb-4 fw-bold uppercase">Name</p>
                                    <h6 className="text-sm fw-bold text-secondary-light mb-0">{customerData.fullName}</h6>
                                </div>
                                <div className="dashed-divider my-4"></div>
                                <div>
                                    <p className="text-xxxxs text-secondary-400 mb-4 fw-bold uppercase">Phone</p>
                                    <h6 className="text-sm fw-bold text-secondary-light mb-0">{customerData.phoneNumber}</h6>
                                </div>
                                <div>
                                    <p className="text-xxxxs text-secondary-400 mb-4 fw-bold uppercase">Pickup Address</p>
                                    <p className="text-xs text-secondary-light mb-0 fw-medium">
                                        {orderData.address?.name}<br />
                                        {orderData.address?.doorNo}, {orderData.address?.street}<br />
                                        {orderData.address?.city} - {orderData.address?.pincode}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
export default ReturnOrderDetailsLayer;
